// TypeScript React
import React, { useState, useEffect, useRef } from 'react';
import { Button, TextField, Box, Paper, Typography, alpha, IconButton } from '@mui/material';
import EdisonDetailsPanel from './EdisonDetailsPanel';
import AgentVegaChart from './AgentVegaChart';
import StreamingText from './StreamingText';
import { EdisonChatMessage, useEdisonAgent } from 'hooks/EdisonAgent';
import Chip from '@mui/material/Chip';
import { keyframes } from '@emotion/react';
import LRSMarkdown from './LRSMarkdown';
import { AiAgentTypes } from '../../../types/ai.types';
import edisonIcon from '../../../assets/edison_icon1.png';
import lumenBolt from '../../../assets/Lumen_bolt.png';
import MessageFeedback from './MessageFeedback';

// Persistent keyframes defined once (prevents restart on each render)
const dotBounce = keyframes({
    '0%,60%,100%': { transform: 'translateY(0)', opacity: 1, scale: '1' },
    '30%': { transform: 'translateY(-6px)', opacity: 0.9, scale: '1' },
});

// Memoized streaming indicator so child dots aren't re-rendered unless streaming flag toggles
// Add timer state and ref at the component level
const StreamingIndicator: React.FC = React.memo(() => {
    const [elapsedTime, setElapsedTime] = useState<number>(0);
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        // Start the timer when component mounts
        const startTime = Date.now();

        timerRef.current = setInterval(() => {
            const currentTime = Date.now();
            setElapsedTime(Math.floor((currentTime - startTime) / 1000)); // Convert to seconds
        }, 100); // Update every 100ms for smooth display

        // Cleanup on unmount
        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, []); // Empty dependency array means this effect runs once on mount

    return (
        <Typography variant="caption" sx={{ display: 'inline-flex', alignItems: 'center' }}>
            Streaming ({elapsedTime}s)
            <Box component="span" sx={{ ml: 0.5, display: 'inline-flex' }}>
                {[0, 1, 2].map((i) => (
                    <Box
                        key={i}
                        component="span"
                        sx={{
                            width: 3,
                            height: 3,
                            borderRadius: '50%',
                            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.55),
                            mx: 0.25,
                            animation: `${dotBounce} 0.9s ${i * 0.15}s infinite ease-in-out`,
                            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                            transform: 'translateZ(0)', // Force hardware acceleration
                            willChange: 'transform', // Optimize animation performance
                            display: 'inline-block', // Ensure consistent box model
                        }}
                    />
                ))}
            </Box>
        </Typography>
    );
});

interface Props {
    isOpen: boolean;
    agentType?: AiAgentTypes;
    contextType?: string;
    contextId?: number;
}

const EdisonChat: React.FunctionComponent<Props> = (props) => {
    const { isOpen, agentType = AiAgentTypes.EDISON, contextType, contextId } = props;
    const { messages, isStreaming, send, reset, createNewThread, submitFeedback } = useEdisonAgent(
        agentType,
        contextType,
        contextId
    );
    const [input, setInput] = useState('');
    const chatEndRef = useRef<HTMLDivElement>(null);
    const textFieldRef = useRef<HTMLTextAreaElement | HTMLInputElement>(null);
    async function onSubmit(e: React.FormEvent | React.KeyboardEvent) {
        e.preventDefault();
        const text = input.trim();
        if (!text) return;
        await send(text);
        setInput('');
    }

    useEffect(() => {
        if (chatEndRef.current) {
            chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages, isStreaming]);

    useEffect(() => {
        if (isOpen) {
            createNewThread();
        }
    }, [isOpen, createNewThread]);

    useEffect(() => {
        if (isOpen) {
            const timer = setTimeout(() => {
                textFieldRef.current?.focus();
            }, 150);
            return () => clearTimeout(timer);
        }
    }, [isOpen]);

    return (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', px: 2, py: 2 }}>
            <Paper
                variant="outlined"
                sx={{ p: 2, flex: 1, overflowY: 'auto', position: 'relative', bgcolor: 'grey.100', mb: 2 }}
            >
                {messages.length === 0 && agentType != AiAgentTypes.RESEARCH ? (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '45%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            textAlign: 'center',
                            width: '80%',
                        }}
                    >
                        <Box
                            component="img"
                            src={edisonIcon}
                            alt="Loading..."
                            sx={{
                                width: 140,
                                height: 140,
                                mx: 0.25,
                                '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                            }}
                        />
                        <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
                            Get started with Edison
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1 }}>
                            {[
                                'What data do you have access to?',
                                'How many commercial properties do we have in Dallas county?',
                                'What is the Q:B ratio for CP renewals in August of each treaty year?',
                                'Generate a chart showing the top 5 states by GL premium',
                            ].map((suggestion, index) => (
                                <Chip
                                    key={index}
                                    label={suggestion}
                                    variant="outlined"
                                    onClick={() => {
                                        setInput(suggestion);
                                        // Optionally, focus the input field
                                        // inputRef.current?.focus(); // If you add a ref to TextField
                                    }}
                                    sx={{ cursor: 'pointer' }}
                                />
                            ))}
                        </Box>
                    </Box>
                ) : (
                    <>
                        {messages.map((m: EdisonChatMessage, i: number) => {
                            const isLastMessage = i === messages.length - 1;
                            const isActivelyStreaming = isLastMessage && isStreaming && m.role === 'assistant';
                            return (
                                <Box
                                    key={i}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: m.role === 'assistant' ? 'flex-start' : 'flex-end',
                                        mb: 2,
                                    }}
                                >
                                    {m.content && (
                                        <Box
                                            sx={{
                                                maxWidth: '90%',
                                                p: 1.5,
                                                borderRadius: 2,
                                                bgcolor: 'white',
                                                padding: '1.5rem',
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                                {m.role === 'assistant' ? (
                                                    <>
                                                        <img
                                                            src={edisonIcon}
                                                            style={{
                                                                width: 45,
                                                                height: 45,
                                                                objectFit: 'contain',
                                                            }}
                                                        />
                                                    </>
                                                ) : (
                                                    <>
                                                        <img
                                                            src={lumenBolt}
                                                            style={{
                                                                width: 35,
                                                                height: 35,
                                                                objectFit: 'contain',
                                                            }}
                                                        />
                                                        <Typography variant="subtitle1" color="text.secondary">
                                                            {m.role.charAt(0).toUpperCase() + m.role.slice(1)}
                                                        </Typography>
                                                    </>
                                                )}
                                            </Box>
                                            <Typography sx={{ fontSize: '14px' }}>
                                                {m.content.length || m.deltaContent ? (
                                                    isActivelyStreaming ? (
                                                        <StreamingText content={m.deltaContent || ''} />
                                                    ) : (
                                                        m.content.map((s, idx) => (
                                                            <Typography key={'contentblock' + idx}>
                                                                <LRSMarkdown>{s}</LRSMarkdown>
                                                            </Typography>
                                                        ))
                                                    )
                                                ) : (
                                                    m.role === 'assistant' &&
                                                    isStreaming && <Typography variant="body1"></Typography>
                                                )}
                                            </Typography>
                                            {m.role === 'assistant' &&
                                                m.details?.charts &&
                                                m.details.charts.length > 0 &&
                                                !isStreaming && (
                                                    <Box sx={{ mt: 2 }}>
                                                        {m.details.charts.map((ch, idx) => (
                                                            <AgentVegaChart key={idx} result={ch} />
                                                        ))}
                                                    </Box>
                                                )}
                                            {m.role === 'assistant' && m.hasDetails && !isStreaming && (
                                                <EdisonDetailsPanel details={m.details} />
                                            )}
                                            {/*/!*--Feedback--*!/*/}
                                            {m.role === 'assistant' && m.requestId && !isStreaming && (
                                                <Box sx={{ mt: 1 }}>
                                                    <MessageFeedback
                                                        requestId={m.requestId}
                                                        submitFeedback={submitFeedback}
                                                    />
                                                    {m.feedback?.submitted && (
                                                        <Typography
                                                            variant="caption"
                                                            sx={{ color: 'success.main', fontStyle: 'italic' }}
                                                        >
                                                            Thank you for your feedback!
                                                        </Typography>
                                                    )}
                                                </Box>
                                            )}
                                        </Box>
                                    )}
                                </Box>
                            );
                        })}
                        {isStreaming && <StreamingIndicator />}
                        <div ref={chatEndRef} /> {/* Anchor for auto-scroll */}
                    </>
                )}
            </Paper>

            <Box component="form" onSubmit={onSubmit} sx={{ mb: 1 }}>
                <TextField
                    inputRef={textFieldRef}
                    fullWidth
                    multiline
                    minRows={1}
                    maxRows={5}
                    size="small"
                    value={input}
                    placeholder="Ask Edison…"
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            onSubmit(e);
                        }
                    }}
                    onChange={(e) => setInput(e.target.value)}
                    disabled={isStreaming}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            background: 'linear-gradient(135deg, #FFDD00 50%, #0084F4 50%)',
                            padding: '3px',
                            borderRadius: '4px',
                            transition: 'box-shadow 0.2s ease-in-out',
                            '& fieldset': {
                                border: 'none',
                            },
                            '&.Mui-focused': {
                                boxShadow: '0 0 0 3px #0084F4, 0 4px 6px rgba(0,0,0,0.2)',
                            },
                        },
                        '& .MuiInputBase-input': {
                            backgroundColor: 'white',
                            borderRadius: '2px',
                            padding: '8.5px 14px',
                        },
                    }}
                />

                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Button type="submit" variant="contained" disabled={isStreaming}>
                        Send
                    </Button>
                    <Button variant="text" onClick={reset} disabled={isStreaming}>
                        Reset
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default EdisonChat;
